// client/src/components/common/TicketAttachments.tsx (TypeScript since ST-00216)
// ST-00221: no upload without a ticket (it went to /ticket-attachments/undefined), and the
// selects are ThemedSelects (CLAUDE.md), not native <select>s.
import {
  useState,
  useEffect,
  useRef,
  useCallback,
  type ChangeEvent,
  type ComponentType,
  type DragEvent,
} from 'react';
import type { AxiosProgressEvent } from 'axios';
import type { IconType } from 'react-icons';
import {
  FaPaperclip,
  FaUpload,
  FaTrash,
  FaTimes,
  FaSpinner,
  FaFileImage,
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFile,
  FaSearchPlus,
  FaChevronLeft,
  FaChevronRight,
  FaEdit,
  FaFilter,
  FaTag,
} from 'react-icons/fa';
import api from '../../services/api';
import ImageAnnotatorJs from './ImageAnnotator';
import ThemedSelect from './ThemedSelect';
import type {
  HttpErrorLike,
  ImageAnnotatorProps,
  RawTicketAttachment,
  TicketAttachment,
} from '../../types/clientShell';

export type { RawTicketAttachment, TicketAttachment };

// ImageAnnotator.js is still JavaScript; its props as it reads them.
const ImageAnnotator = ImageAnnotatorJs as unknown as ComponentType<ImageAnnotatorProps>;

const CATEGORIES = [
  'Weighbridge Ticket',
  'Delivery Note',
  'Damage Photo',
  'Supporting Document',
  'Other',
];

const CATEGORY_COLORS: Readonly<Record<string, string>> = {
  'Weighbridge Ticket': 'bg-blue-100 text-blue-700',
  'Delivery Note': 'bg-green-100 text-green-700',
  'Damage Photo': 'bg-red-100 text-red-700',
  'Supporting Document': 'bg-purple-100 text-purple-700',
  Other: 'bg-gray-100 text-gray-600',
};

// GET /api/ticket-attachments/:uid returns attachment_id, mime_type, file_size and
// category in snake_case (only the other columns are aliased), while the timeline
// and this panel use the PascalCase names of the old mssql API. Reading the
// PascalCase keys alone left delete/category/preview/annotate dead and every size
// "NaN MB" (AMD-16). Accept either shape.
// The id, names and upload stamp are always sent in one of the two spellings.
export const normalizeAttachment = (a: RawTicketAttachment): TicketAttachment => {
  const pick = <K extends keyof RawTicketAttachment>(...keys: K[]) =>
    keys.map((k) => a[k]).find((v) => v !== undefined && v !== null);
  const size = Number(pick('FileSize', 'file_size'));
  return {
    ...a,
    AttachmentID: pick('AttachmentID', 'attachment_id') as number,
    TicketUID: pick('TicketUID', 'ticket_uid') as string,
    OriginalFilename: pick('OriginalFilename', 'original_filename') as string,
    StoredFilename: pick('StoredFilename', 'stored_filename') as string,
    MimeType: pick('MimeType', 'mime_type') || null,
    FileSize: Number.isFinite(size) ? size : null,
    Category: pick('Category', 'category') || null,
    UploadedBy: pick('UploadedBy', 'uploaded_by') as string,
    UploadedAt: pick('UploadedAt', 'uploaded_at') as string,
    AnnotationData: pick('AnnotationData', 'annotation_data') || null,
  };
};

const formatFileSize = (bytes: number | null | undefined): string => {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes)) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getFileIcon = (mimeType: string | null | undefined): IconType => {
  if (mimeType?.startsWith('image/')) return FaFileImage;
  if (mimeType === 'application/pdf') return FaFilePdf;
  if (mimeType?.includes('word') || mimeType?.includes('document')) return FaFileWord;
  if (mimeType?.includes('excel') || mimeType?.includes('sheet') || mimeType === 'text/csv')
    return FaFileExcel;
  return FaFile;
};

const isPreviewable = (mimeType: string | null | undefined) =>
  mimeType?.startsWith('image/') || mimeType === 'application/pdf';

interface PreviewModalProps {
  attachment: TicketAttachment;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  hasPrev: boolean;
  hasNext: boolean;
  onAnnotate: (attachment: TicketAttachment) => void;
}

const PreviewModal = ({
  attachment,
  onClose,
  onPrev,
  onNext,
  hasPrev,
  hasNext,
  onAnnotate,
}: PreviewModalProps) => {
  const url = api.ticketAttachments.fileUrl(attachment.StoredFilename);
  const canAnnotate = attachment.MimeType?.startsWith('image/');

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && hasPrev) onPrev();
      if (e.key === 'ArrowRight' && hasNext) onNext();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose, onPrev, onNext, hasPrev, hasNext]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70"
      onClick={onClose}
    >
      <div
        className="relative max-w-[90vw] max-h-[90vh] flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute -top-10 left-0 right-0 flex items-center justify-between px-2 text-white text-sm">
          <span className="truncate max-w-[60%]">{attachment.OriginalFilename}</span>
          <div className="flex items-center gap-2">
            {canAnnotate && (
              <button
                onClick={() => onAnnotate(attachment)}
                className="flex items-center gap-1 px-2 py-1 text-xs bg-surface/20 hover:bg-surface/30 rounded transition-colors"
              >
                <FaEdit className="w-3 h-3" /> Annotate
              </button>
            )}
            <button onClick={onClose} className="p-1 hover:bg-surface/20 rounded transition-colors">
              <FaTimes className="w-5 h-5" />
            </button>
          </div>
        </div>

        {hasPrev && (
          <button
            onClick={onPrev}
            className="absolute left-[-48px] top-1/2 -translate-y-1/2 p-2 text-white hover:bg-surface/20 rounded-full transition-colors"
          >
            <FaChevronLeft className="w-5 h-5" />
          </button>
        )}
        {hasNext && (
          <button
            onClick={onNext}
            className="absolute right-[-48px] top-1/2 -translate-y-1/2 p-2 text-white hover:bg-surface/20 rounded-full transition-colors"
          >
            <FaChevronRight className="w-5 h-5" />
          </button>
        )}

        {attachment.MimeType?.startsWith('image/') ? (
          <img
            src={url}
            alt={attachment.OriginalFilename}
            className="max-w-full max-h-[85vh] object-contain rounded shadow-2xl"
          />
        ) : attachment.MimeType === 'application/pdf' ? (
          <iframe
            src={url}
            title={attachment.OriginalFilename}
            className="w-[80vw] h-[85vh] rounded shadow-2xl bg-surface"
          />
        ) : null}
      </div>
    </div>
  );
};

export interface TicketAttachmentsProps {
  ticketUID?: string | null;
  /** Accepted for callers; the panel has one layout. */
  compact?: boolean;
}

const TicketAttachments = ({ ticketUID, compact: _compact = false }: TicketAttachmentsProps) => {
  const [attachments, setAttachments] = useState<TicketAttachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('Other');
  const [filterCategory, setFilterCategory] = useState('All');
  const [showCategoryFilter, setShowCategoryFilter] = useState(false);
  const [annotatingAttachment, setAnnotatingAttachment] = useState<TicketAttachment | null>(null);
  const [editingCategoryId, setEditingCategoryId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchAttachments = useCallback(async () => {
    if (!ticketUID) return;
    setLoading(true);
    try {
      const res = await api.ticketAttachments.list(ticketUID);
      const rows: unknown = res.data;
      setAttachments(
        Array.isArray(rows) ? (rows as RawTicketAttachment[]).map(normalizeAttachment) : []
      );
    } catch (err) {
      console.error('Error fetching attachments:', err);
    } finally {
      setLoading(false);
    }
  }, [ticketUID]);

  useEffect(() => {
    fetchAttachments();
  }, [fetchAttachments]);

  const handleUpload = async (files: FileList | null) => {
    if (!ticketUID || !files || files.length === 0) return;
    setError(null);
    setUploading(true);
    setUploadProgress(0);
    try {
      await api.ticketAttachments.upload(
        ticketUID,
        Array.from(files),
        (e: AxiosProgressEvent) => {
          if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
        },
        selectedCategory
      );
      await fetchAttachments();
    } catch (err) {
      const e = err as HttpErrorLike;
      const msg = e.response?.data?.message || e.message || 'Upload failed.';
      setError(msg);
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (attachmentId: number) => {
    if (!ticketUID) return;
    setDeletingId(attachmentId);
    try {
      await api.ticketAttachments.remove(ticketUID, attachmentId);
      setAttachments((prev) => prev.filter((a) => a.AttachmentID !== attachmentId));
    } catch (err) {
      setError((err as HttpErrorLike).response?.data?.message || 'Delete failed.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleCategoryChange = async (attachmentId: number, newCategory: string) => {
    if (!ticketUID) return;
    try {
      await api.ticketAttachments.update(ticketUID, attachmentId, { category: newCategory });
      setAttachments((prev) =>
        prev.map((a) => (a.AttachmentID === attachmentId ? { ...a, Category: newCategory } : a))
      );
    } catch (err) {
      setError((err as HttpErrorLike).response?.data?.message || 'Failed to update category.');
    }
    setEditingCategoryId(null);
  };

  const handleAnnotationSave = async (dataUrl: string) => {
    if (!annotatingAttachment || !ticketUID) return;
    try {
      const blob = await fetch(dataUrl).then((r) => r.blob());
      const file = new File([blob], annotatingAttachment.OriginalFilename, { type: 'image/png' });
      await api.ticketAttachments.replaceFile(ticketUID, annotatingAttachment.AttachmentID, file);
      setAnnotatingAttachment(null);
      await fetchAttachments();
    } catch (err) {
      setError((err as HttpErrorLike).response?.data?.message || 'Failed to save markup.');
    }
  };

  const handleDrag = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.length > 0) handleUpload(e.dataTransfer.files);
  };

  // Nothing to attach to yet: no upload control (it would post to /ticket-attachments/undefined)
  if (!ticketUID) {
    return (
      <div className="space-y-1">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <FaPaperclip className="w-3.5 h-3.5 text-primary-600" />
          <span>Attachments</span>
        </div>
        <p className="text-xs text-gray-400">No ticket selected.</p>
      </div>
    );
  }

  const filteredAttachments =
    filterCategory === 'All'
      ? attachments
      : attachments.filter((a) => (a.Category || 'Other') === filterCategory);

  const previewableAttachments = filteredAttachments.filter((a) => isPreviewable(a.MimeType));

  const openPreview = (attachment: TicketAttachment) => {
    const idx = previewableAttachments.findIndex((a) => a.AttachmentID === attachment.AttachmentID);
    if (idx !== -1) setPreviewIndex(idx);
  };

  const categoryCountMap: Record<string, number> = {};
  attachments.forEach((a) => {
    const cat = a.Category || 'Other';
    categoryCountMap[cat] = (categoryCountMap[cat] || 0) + 1;
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <FaPaperclip className="w-3.5 h-3.5 text-primary-600" />
          <span>Attachments</span>
          {attachments.length > 0 && (
            <span className="text-xs text-gray-400 font-normal">({attachments.length}/20)</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {attachments.length > 0 && (
            <button
              type="button"
              onClick={() => setShowCategoryFilter(!showCategoryFilter)}
              className={`inline-flex items-center gap-1 px-2 py-1.5 text-xs rounded-md border transition-colors ${
                showCategoryFilter || filterCategory !== 'All'
                  ? 'text-primary-700 bg-primary-50 border-primary-200'
                  : 'text-gray-500 bg-surface border-gray-200 hover:bg-gray-50'
              }`}
            >
              <FaFilter className="w-2.5 h-2.5" />
              {filterCategory !== 'All' && <span>{filterCategory}</span>}
            </button>
          )}
          {attachments.length < 20 && (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-md hover:bg-primary-100 disabled:opacity-50 transition-colors"
            >
              {uploading ? (
                <FaSpinner className="w-3 h-3 animate-spin" />
              ) : (
                <FaUpload className="w-3 h-3" />
              )}
              {uploading ? `Uploading ${uploadProgress}%` : 'Upload Files'}
            </button>
          )}
        </div>
      </div>

      {/* Category filter pills */}
      {showCategoryFilter && (
        <div className="flex flex-wrap gap-1.5">
          <button
            onClick={() => setFilterCategory('All')}
            className={`px-2 py-1 text-[11px] rounded-full border transition-colors ${
              filterCategory === 'All'
                ? 'bg-primary-500/10 text-primary-700 border-primary-500'
                : 'bg-surface text-gray-600 border-gray-200 hover:bg-gray-50'
            }`}
          >
            All ({attachments.length})
          </button>
          {CATEGORIES.map((cat) => {
            const count = categoryCountMap[cat] || 0;
            if (count === 0 && filterCategory !== cat) return null;
            return (
              <button
                key={cat}
                onClick={() => setFilterCategory(filterCategory === cat ? 'All' : cat)}
                className={`px-2 py-1 text-[11px] rounded-full border transition-colors ${
                  filterCategory === cat
                    ? 'bg-primary-500/10 text-primary-700 border-primary-500'
                    : `${CATEGORY_COLORS[cat]} border-transparent`
                }`}
              >
                {cat} ({count})
              </button>
            );
          })}
        </div>
      )}

      {/* Upload category selector */}
      {attachments.length < 20 && (
        <div className="flex items-center gap-2">
          <FaTag className="w-3 h-3 text-gray-400" />
          <span className="text-[10px] text-gray-500 uppercase tracking-wider">Upload as:</span>
          <ThemedSelect
            aria-label="Upload as"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(String(e.target.value))}
            className="min-w-[11rem] text-xs h-7"
          >
            {CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </ThemedSelect>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
        className="hidden"
        onChange={(e: ChangeEvent<HTMLInputElement>) => handleUpload(e.target.files)}
      />

      {/* Drop zone */}
      {(attachments.length === 0 || dragActive) && (
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-lg p-4 text-center text-xs transition-colors ${
            dragActive
              ? 'border-primary-500 bg-primary-50 text-primary-700'
              : 'border-gray-300 text-gray-400 hover:border-gray-400'
          }`}
        >
          {uploading ? (
            <div className="space-y-2">
              <FaSpinner className="w-5 h-5 mx-auto animate-spin text-primary-500" />
              <p>Uploading... {uploadProgress}%</p>
              <div className="w-48 mx-auto h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary-500 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          ) : (
            <div>
              <FaUpload className="w-5 h-5 mx-auto mb-1 text-gray-300" />
              <p>
                Drop files here or{' '}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="text-primary-600 hover:underline font-medium"
                >
                  browse
                </button>
              </p>
              <p className="mt-1 text-[10px] text-gray-400">
                Images, PDF, Word, Excel, CSV — max 10 MB each
              </p>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="flex items-center justify-between p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
          <span>{error}</span>
          <button
            type="button"
            onClick={() => setError(null)}
            className="ml-2 text-red-400 hover:text-red-600"
          >
            <FaTimes className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Attachment list */}
      {loading ? (
        <div className="flex items-center justify-center py-3 text-xs text-gray-400">
          <FaSpinner className="w-3.5 h-3.5 animate-spin mr-2" /> Loading attachments...
        </div>
      ) : (
        filteredAttachments.length > 0 && (
          <div
            className="space-y-1.5 max-h-48 overflow-y-auto"
            onDragEnter={handleDrag}
            onDragOver={handleDrag}
            onDragLeave={handleDrag}
            onDrop={handleDrop}
          >
            {filteredAttachments.map((att) => {
              const Icon = getFileIcon(att.MimeType);
              const canPreview = isPreviewable(att.MimeType);
              const canAnnotate = att.MimeType?.startsWith('image/');
              const fileUrl = api.ticketAttachments.fileUrl(att.StoredFilename);
              const isDeleting = deletingId === att.AttachmentID;
              const category = att.Category || 'Other';
              const hasAnnotations = att.AnnotationData && att.AnnotationData !== '[]';

              return (
                <div
                  key={att.AttachmentID}
                  className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-md border border-gray-200 group hover:border-gray-300 transition-colors"
                >
                  {/* Thumbnail for images */}
                  {att.MimeType?.startsWith('image/') ? (
                    <div
                      className="w-8 h-8 rounded overflow-hidden flex-shrink-0 bg-gray-200 cursor-pointer relative"
                      onClick={() => openPreview(att)}
                    >
                      <img src={fileUrl} alt="" className="w-full h-full object-cover" />
                      {hasAnnotations && (
                        <div
                          className="absolute -top-1 -right-1 w-3 h-3 bg-amber-400 rounded-full border border-white"
                          title="Has annotations"
                        />
                      )}
                    </div>
                  ) : (
                    <Icon className="w-4 h-4 flex-shrink-0 text-gray-400" />
                  )}

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      {canPreview ? (
                        <button
                          type="button"
                          onClick={() => openPreview(att)}
                          className="text-xs font-medium text-gray-700 hover:text-primary-600 truncate text-left"
                          title={att.OriginalFilename}
                        >
                          {att.OriginalFilename}
                        </button>
                      ) : (
                        <a
                          href={fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs font-medium text-gray-700 hover:text-primary-600 truncate"
                          title={att.OriginalFilename}
                        >
                          {att.OriginalFilename}
                        </a>
                      )}
                      {editingCategoryId === att.AttachmentID ? (
                        <ThemedSelect
                          aria-label={`Category of ${att.OriginalFilename}`}
                          value={category}
                          onChange={(e) =>
                            handleCategoryChange(att.AttachmentID, String(e.target.value))
                          }
                          onBlur={() => setEditingCategoryId(null)}
                          autoFocus
                          defaultMenuIsOpen
                          className="min-w-[11rem] text-xs h-7"
                        >
                          {CATEGORIES.map((cat) => (
                            <option key={cat} value={cat}>
                              {cat}
                            </option>
                          ))}
                        </ThemedSelect>
                      ) : (
                        <button
                          onClick={() => setEditingCategoryId(att.AttachmentID)}
                          className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${CATEGORY_COLORS[category]} cursor-pointer hover:opacity-80 transition-opacity`}
                          title="Click to change category"
                        >
                          {category}
                        </button>
                      )}
                    </div>
                    <p className="text-[10px] text-gray-400">
                      {formatFileSize(att.FileSize)} — {att.UploadedBy} —{' '}
                      {new Date(att.UploadedAt).toLocaleString()}
                    </p>
                  </div>

                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {canAnnotate && (
                      <button
                        type="button"
                        onClick={() => setAnnotatingAttachment(att)}
                        className="p-1 text-gray-400 hover:text-amber-600 rounded transition-colors"
                        title="Annotate"
                      >
                        <FaEdit className="w-3 h-3" />
                      </button>
                    )}
                    {canPreview && (
                      <button
                        type="button"
                        onClick={() => openPreview(att)}
                        className="p-1 text-gray-400 hover:text-primary-600 rounded transition-colors"
                        title="Preview"
                      >
                        <FaSearchPlus className="w-3 h-3" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleDelete(att.AttachmentID)}
                      disabled={isDeleting}
                      className="p-1 text-gray-400 hover:text-red-600 rounded transition-colors disabled:opacity-50"
                      title="Delete"
                    >
                      {isDeleting ? (
                        <FaSpinner className="w-3 h-3 animate-spin" />
                      ) : (
                        <FaTrash className="w-3 h-3" />
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {/* Preview modal */}
      {previewIndex !== null && previewableAttachments[previewIndex] && (
        <PreviewModal
          attachment={previewableAttachments[previewIndex]}
          onClose={() => setPreviewIndex(null)}
          onPrev={() => setPreviewIndex((i) => Math.max(0, i! - 1))}
          onNext={() => setPreviewIndex((i) => Math.min(previewableAttachments.length - 1, i! + 1))}
          hasPrev={previewIndex > 0}
          hasNext={previewIndex < previewableAttachments.length - 1}
          onAnnotate={(att) => {
            setPreviewIndex(null);
            setAnnotatingAttachment(att);
          }}
        />
      )}

      {/* Annotation editor */}
      {annotatingAttachment && (
        <ImageAnnotator
          imageUrl={api.ticketAttachments.fileUrl(annotatingAttachment.StoredFilename)}
          onSave={handleAnnotationSave}
          onClose={() => setAnnotatingAttachment(null)}
        />
      )}
    </div>
  );
};

export default TicketAttachments;
